import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getConsentState } from '../utils/consent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SiteContext = createContext({});

export const trackEvent = (eventName) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName });

  if (window.fbq && window.__marketingConsentGranted) {
    if (eventName === 'whatsapp_click' || eventName === 'call_click') {
      window.fbq('track', 'Contact');
    } else if (eventName === 'form_submit') {
      window.fbq('track', 'Lead');
    }
  }
};

export const SiteProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/content/all`);
      if (res.ok) {
        const result = await res.json();
        setData(result);
        injectTracking(result.settings);
      }
    } catch (e) {
      console.error('Failed to load site content:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  useEffect(() => {
    const handleConsentChange = (e) => {
      if (e.detail?.marketing && window.__siteSettings) {
        injectTracking(window.__siteSettings);
      }
    };
    window.addEventListener('consentChanged', handleConsentChange);
    return () => window.removeEventListener('consentChanged', handleConsentChange);
  }, []);

  return (
    <SiteContext.Provider value={{ ...data, loading, refetch: fetchContent }}>
      {children}
    </SiteContext.Provider>
  );
};

function injectTracking(settings) {
  if (!settings) return;
  window.__siteSettings = settings;

  const hasMarketingConsent = window.__marketingConsentGranted === true;

  if (settings.gtmId && !document.getElementById('gtm-script') && hasMarketingConsent) {
    const s = document.createElement('script');
    s.id = 'gtm-script';
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${settings.gtmId}');`;
    document.head.appendChild(s);
    const ns = document.createElement('noscript');
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${settings.gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(ns, document.body.firstChild);
  }

  if (settings.metaPixelId && !document.getElementById('fb-pixel-script') && hasMarketingConsent) {
    const s = document.createElement('script');
    s.id = 'fb-pixel-script';
    s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${settings.metaPixelId}');fbq('track','PageView');`;
    document.head.appendChild(s);
    const ns = document.createElement('noscript');
    ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${settings.metaPixelId}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(ns);
  }
}

export const useSiteContent = () => useContext(SiteContext);
export default SiteContext;
